// agents/src/services/notificationService.js

/**
 * Servicio de notificaciones desktop usando la API de Notifications del navegador
 */

class NotificationService {
  constructor() {
    this.permission = 'default';
    this.enabled = false;
    this.soundEnabled = true;
    this.notificationSound = null;
    this.audioReady = false; // Flag para saber si el audio está listo
    this.init();
  }

  /**
   * Inicializar el servicio de notificaciones
   */
  init() {
    // Verificar si el navegador soporta notificaciones
    if (!('Notification' in window)) {
      console.warn('[NotificationService] Este navegador no soporta notificaciones desktop');
      return;
    }

    this.permission = Notification.permission;
    this.enabled = this.permission === 'granted';

    // Cargar preferencias guardadas
    this.loadPreferences();

    // Crear audio para notificaciones
    this.notificationSound = new Audio('/notification.mp3');
    this.notificationSound.volume = 0.5;

    // Intentar "preparar" el audio en la primera interacción del usuario
    this.setupAudioOnInteraction();
  }

  /**
   * Preparar el audio en la primera interacción del usuario
   * Esto es necesario porque los navegadores bloquean autoplay hasta que haya interacción
   */
  setupAudioOnInteraction() {
    const prepareAudio = () => {
      if (this.notificationSound && !this.audioReady) {
        // Intentar reproducir y pausar inmediatamente
        this.notificationSound.play()
          .then(() => {
            this.notificationSound.pause();
            this.notificationSound.currentTime = 0;
            this.audioReady = true;
            console.log('[NotificationService] ✅ Audio preparado para notificaciones');
          })
          .catch(() => {
            // Si falla, no es crítico, simplemente las notificaciones no tendrán sonido
            console.log('[NotificationService] ℹ️ Audio no disponible (requiere interacción del usuario)');
          });

        // Remover los listeners después de la primera interacción exitosa
        document.removeEventListener('click', prepareAudio);
        document.removeEventListener('keydown', prepareAudio);
        document.removeEventListener('touchstart', prepareAudio);
      }
    };

    // Escuchar la primera interacción del usuario
    document.addEventListener('click', prepareAudio, { once: true });
    document.addEventListener('keydown', prepareAudio, { once: true });
    document.addEventListener('touchstart', prepareAudio, { once: true });
  }

  /**
   * Solicitar permiso para mostrar notificaciones
   */
  async requestPermission() {
    if (!('Notification' in window)) {
      return false;
    }

    if (this.permission === 'granted') {
      return true;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      this.enabled = permission === 'granted';
      this.savePreferences();
      return permission === 'granted';
    } catch (error) {
      console.error('[NotificationService] Error solicitando permiso:', error);
      return false;
    }
  }

  /**
   * Mostrar una notificación desktop
   * @param {string} title - Título de la notificación
   * @param {object} options - Opciones de la notificación
   */
  show(title, options = {}) {
    if (!this.enabled || this.permission !== 'granted') {
      console.warn('[NotificationService] Notificaciones no habilitadas');
      return null;
    }

    // Verificar si la pestaña está enfocada
    if (document.hasFocus()) {
      console.log('[NotificationService] Pestaña enfocada, no mostrar notificación');
      return null;
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: 'whatsapp-notification',
      requireInteraction: false,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);

      // Reproducir sonido si está habilitado y el audio está listo
      if (this.soundEnabled && this.notificationSound && this.audioReady) {
        this.notificationSound.currentTime = 0; // Reiniciar al inicio
        this.notificationSound.play().catch(err => {
          console.warn('[NotificationService] No se pudo reproducir sonido:', err.message);
        });
      }

      // Auto-cerrar después de 5 segundos
      setTimeout(() => {
        notification.close();
      }, 5000);

      return notification;
    } catch (error) {
      console.error('[NotificationService] Error mostrando notificación:', error);
      return null;
    }
  }

  /**
   * Notificación de nuevo mensaje
   * @param {string} from - Remitente del mensaje
   * @param {string} preview - Vista previa del mensaje
   * @param {function} onClick - Callback al hacer click
   */
  notifyNewMessage(from, preview, onClick) {
    const notification = this.show('Nuevo mensaje de WhatsApp', {
      body: `${from}: ${preview}`,
      tag: `message-${from}`,
      data: { from, type: 'message' }
    });

    if (notification && onClick) {
      notification.onclick = () => {
        window.focus();
        onClick();
        notification.close();
      };
    }

    return notification;
  }

  /**
   * Notificación de conversación sin respuesta
   * @param {string} from - Número del cliente
   * @param {number} waitTime - Tiempo de espera en minutos
   * @param {function} onClick - Callback al hacer click
   */
  notifyNoResponse(from, waitTime, onClick) {
    const notification = this.show('⏳ Conversación sin respuesta', {
      body: `${from} lleva ${waitTime} min esperando respuesta`,
      tag: `no-response-${from}`,
      requireInteraction: true,
      data: { from, type: 'no-response' }
    });

    if (notification && onClick) {
      notification.onclick = () => {
        window.focus();
        onClick();
        notification.close();
      };
    }

    return notification;
  }

  /**
   * Notificación de agente requerido
   * @param {string} from - Número del cliente
   * @param {function} onClick - Callback al hacer click
   */
  notifyAgentRequired(from, onClick) {
    const notification = this.show('🔔 Agente requerido', {
      body: `${from} requiere atención de un agente`,
      tag: `agent-required-${from}`,
      requireInteraction: true,
      data: { from, type: 'agent-required' }
    });

    if (notification && onClick) {
      notification.onclick = () => {
        window.focus();
        onClick();
        notification.close();
      };
    }

    return notification;
  }

  /**
   * Habilitar/deshabilitar notificaciones
   */
  toggle() {
    this.enabled = !this.enabled;
    this.savePreferences();
    return this.enabled;
  }

  /**
   * Habilitar/deshabilitar sonido
   */
  toggleSound() {
    this.soundEnabled = !this.soundEnabled;
    this.savePreferences();
    return this.soundEnabled;
  }

  /**
   * Guardar preferencias en localStorage
   */
  savePreferences() {
    const preferences = {
      enabled: this.enabled,
      soundEnabled: this.soundEnabled
    };
    localStorage.setItem('notificationPreferences', JSON.stringify(preferences));
  }

  /**
   * Cargar preferencias desde localStorage
   */
  loadPreferences() {
    try {
      const saved = localStorage.getItem('notificationPreferences');
      if (saved) {
        const preferences = JSON.parse(saved);
        this.enabled = preferences.enabled !== undefined ? preferences.enabled : this.enabled;
        this.soundEnabled = preferences.soundEnabled !== undefined ? preferences.soundEnabled : this.soundEnabled;
      }
    } catch (error) {
      console.error('[NotificationService] Error cargando preferencias:', error);
    }
  }

  /**
   * Obtener estado actual
   */
  getStatus() {
    return {
      supported: 'Notification' in window,
      permission: this.permission,
      enabled: this.enabled,
      soundEnabled: this.soundEnabled
    };
  }
}

// Exportar instancia única (singleton)
export const notificationService = new NotificationService();
export default notificationService;
